/**
 * Mi tienda (/mi-tienda/*): the seller's own area. One table says where each section lives, so the
 * panel's menu, the links in emails and pushes, and the old /cuenta/vender?tab= addresses agree.
 */

export const SELLER_SECTIONS = ["inicio", "publicaciones", "publicar", "pedidos", "ofertas", "clientes", "cupones", "emails", "tienda", "demanda", "estadisticas", "est_cartas", "est_precios", "plan", "ajustes", "integraciones"] as const;
export type SellerSection = (typeof SELLER_SECTIONS)[number];

export const PANEL = "/mi-tienda";

const PATHS: Record<SellerSection, string> = {
  inicio: PANEL,
  publicaciones: `${PANEL}/publicaciones`,
  publicar: `${PANEL}/publicar`,
  pedidos: `${PANEL}/ventas`,
  ofertas: `${PANEL}/ventas?f=ofertas`,
  clientes: `${PANEL}/clientes`,
  cupones: `${PANEL}/clientes/cupones`,
  emails: `${PANEL}/clientes/emails`,
  tienda: `${PANEL}/tienda`,
  demanda: `${PANEL}/demanda`,
  estadisticas: `${PANEL}/estadisticas`,
  est_cartas: `${PANEL}/estadisticas/cartas`,
  est_precios: `${PANEL}/estadisticas/precios`,
  plan: `${PANEL}/plan`,
  ajustes: `${PANEL}/ajustes`,
  integraciones: `${PANEL}/integraciones`,
};

/** Where a section lives ("/mi-tienda/ventas?f=ofertas"). */
export const sellerPath = (s: SellerSection): string => PATHS[s];

/** An order's page for each side: the seller's lives in the panel, the buyer's in the market. */
export const orderPath = (number: string, role: "buyer" | "seller"): string => (role === "seller" ? `${PANEL}/ventas/${encodeURIComponent(number)}` : `/mercado/pedido/${encodeURIComponent(number)}`);

/** Sections shown as sub-tabs of one menu entry. */
export const SECTION_GROUPS = { ventas: ["pedidos", "ofertas"], clientes: ["clientes", "cupones", "emails"], estadisticas: ["estadisticas", "est_cartas", "est_precios"] } as const satisfies Record<string, readonly SellerSection[]>;

export type PanelPlace = { section: SellerSection } | { order: string } | null;

/** What a panel URL shows: a section, one of the shop's orders, or nothing (an unknown address). */
export function panelPlace(pathname: string, search = ""): PanelPlace {
  const path = pathname.replace(/\/+$/, "") || "/";
  if (path === PANEL) return { section: "inicio" };
  if (!path.startsWith(`${PANEL}/`)) return null;
  const rest = path.slice(PANEL.length + 1).split("/");
  if (rest[0] === "ventas" && rest.length === 2 && rest[1]) return { order: decodeURIComponent(rest[1]) };
  if (rest[0] === "ventas" && rest.length === 1) return { section: new URLSearchParams(search).get("f") === "ofertas" ? "ofertas" : "pedidos" };
  if (rest[0] === "clientes" && rest.length === 2 && (rest[1] === "cupones" || rest[1] === "emails")) return { section: rest[1] };
  if (rest[0] === "estadisticas" && rest.length === 2 && (rest[1] === "cartas" || rest[1] === "precios")) return { section: rest[1] === "cartas" ? "est_cartas" : "est_precios" };
  if (rest.length === 1 && (SELLER_SECTIONS as readonly string[]).includes(rest[0]) && !["inicio", "pedidos", "ofertas", "cupones", "emails", "est_cartas", "est_precios"].includes(rest[0])) return { section: rest[0] as SellerSection };
  return null;
}

/** Before /mi-tienda: the ?tab= (and grouped ?tab=&s=) values the panel had, and where each one is now. */
const LEGACY_TABS: Record<string, SellerSection> = {
  inicio: "inicio",
  publicaciones: "publicaciones",
  ventas: "pedidos",
  pedidos: "pedidos",
  ofertas: "ofertas",
  clientes: "clientes",
  cupones: "cupones",
  emails: "emails",
  mas: "estadisticas",
  demanda: "demanda",
  estadisticas: "estadisticas",
  plan: "plan",
  ajustes: "ajustes",
};

/**
 * /cuenta/vender?tab=… (old emails, pushes, bookmarks, Mercado Pago's stored return addresses) → the
 * same place in the panel, keeping every other parameter (?mp=ok, ?suscripcion=1).
 */
export function legacySellerUrl(search: string): string {
  const params = new URLSearchParams(search);
  const tab = params.get("tab") ?? "";
  const s = params.get("s") ?? "";
  // A Mercado Pago subscription started before the panel moved comes back to ?tab=ajustes&suscripcion=1.
  const section: SellerSection = params.has("suscripcion") ? "plan" : (LEGACY_TABS[s] ?? LEGACY_TABS[tab] ?? "inicio");
  params.delete("tab");
  params.delete("s");
  const [path, own] = sellerPath(section).split("?");
  const merged = new URLSearchParams(own ?? "");
  params.forEach((v, k) => merged.set(k, v));
  const query = merged.toString();
  return query ? `${path}?${query}` : path;
}
